/**
 * SkillEffectDeclarationEngine — runtime that reads skill-effect-declarations.json
 * and resolves declared status effects at the right triggers.
 *
 * Architectural sibling of SkillVFXOrchestrator: loads declarations from JSON,
 * indexes by skillId, filters by trigger, evaluates conditions, and resolves
 * parameters into ApplyOptions. Entries marked `imperative: true` are skipped —
 * they exist in the catalog for documentation but are handled by skill code.
 *
 * The engine does NOT call StatusEffectTracker directly. It provides resolved
 * effect descriptions that the factory adapter routes to the appropriate tracker.
 *
 * @see ADR-087 (skill effect declarations)
 * @see ADR-065 (CC hierarchy)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type {
  EffectCondition,
  EffectDeclaration,
  EffectTrigger,
  ParamSource,
  SkillEffectDeclaration,
  SkillEffectDeclarationsFile,
} from '../utils/Types';
import type { ApplyOptions } from './StatusEffectTracker';

import declarationsData from '../data/skill-effect-declarations.json';

// ---------------------------------------------------------------------------
// Resolver context — provided by the factory adapter at call time
// ---------------------------------------------------------------------------

/**
 * Context supplied by the factory adapter when requesting effect resolution.
 * Contains the resolver functions needed to evaluate conditions and params.
 */
export interface ResolverContext {
  /** Resolver query: additive effect total for a skill key. */
  getAdditiveEffect(skillId: string, key: string): number;
  /** Resolver query: max effect value for a skill key. */
  getMaxEffect(skillId: string, key: string): number;
  /** Resolver query: multiplicative effect total for a skill key. */
  getMultiplicativeEffect(skillId: string, key: string): number;
  /** Resolver query: tree passive effect. */
  getTreePassiveEffect(tree: string, key: string, mode: 'sum' | 'max'): number;
  /** Whether a resolver key has any non-zero effect (upgrade presence check). */
  hasEffect(skillId: string, key: string): boolean;
  /** Check immunity on the target tracker. */
  isImmune(effectId: string): boolean;
  /** Current charge ratio (0–1) for charge-based skills. */
  chargeRatio?: number;
  /** Target's combat type — 'ranged' or 'melee'. */
  targetType?: 'ranged' | 'melee';
  /** Target's defense type — 'armored', 'shield', 'unarmored', etc. */
  defenseType?: string;
}

// ---------------------------------------------------------------------------
// Resolved output — what the factory adapter uses to call tracker.apply()
// ---------------------------------------------------------------------------

/** Fully resolved effect ready for application. */
export interface ResolvedEffect {
  effectId: string;
  target: 'enemy' | 'self';
  source: string;
  applyOptions: ApplyOptions;
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class SkillEffectDeclarationEngine {
  /** Declarative entries indexed by skillId. Imperative entries are excluded. */
  private readonly declarationsBySkill: Map<string, EffectDeclaration[]> =
    new Map();

  constructor() {
    this.loadDeclarations();
  }

  // -------------------------------------------------------------------------
  // Initialization
  // -------------------------------------------------------------------------

  private loadDeclarations(): void {
    const data = declarationsData as SkillEffectDeclarationsFile;
    for (const decl of data.declarations) {
      if (decl.imperative) continue;
      this.declarationsBySkill.set(decl.skillId, decl.effects);
    }
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Get all declared effects for a skill + trigger combination.
   * Returns an empty array if no declarations exist or the skill is imperative.
   */
  getDeclarations(
    skillId: string,
    trigger: EffectTrigger,
  ): EffectDeclaration[] {
    const effects = this.declarationsBySkill.get(skillId);
    if (!effects) return [];
    return effects.filter((e) => e.trigger === trigger);
  }

  /** Whether the engine has any declarative entries for a skill. */
  has(skillId: string): boolean {
    return this.declarationsBySkill.has(skillId);
  }

  /**
   * Resolve a single declaration into a fully resolved effect ready for
   * tracker.apply(). Evaluates all conditions — returns null if any fail.
   */
  resolve(
    skillId: string,
    decl: EffectDeclaration,
    ctx: ResolverContext,
  ): ResolvedEffect | null {
    // Gate: all conditions must pass (AND logic)
    if (decl.conditions) {
      for (const cond of decl.conditions) {
        if (!this.evaluateCondition(cond, ctx)) return null;
      }
    }

    // Resolve parameters into concrete numbers
    const applyOptions: ApplyOptions = {
      sourceSkillId: decl.source ?? skillId,
      durationMs: this.resolveParam(decl.params.durationMs, ctx),
    };

    if (decl.params.intensity != null) {
      applyOptions.intensity = this.resolveParam(decl.params.intensity, ctx);
    }

    if (decl.params.stacks != null) {
      applyOptions.stacks = this.resolveParam(decl.params.stacks, ctx);
    }

    if (decl.params.stackingRuleOverride != null) {
      applyOptions.stackingRuleOverride = decl.params.stackingRuleOverride;
    }

    return {
      effectId: decl.effectId,
      target: decl.target ?? 'enemy',
      source: decl.source ?? skillId,
      applyOptions,
    };
  }

  /**
   * Convenience: get declarations for a trigger, resolve each, and return
   * only those whose conditions passed. Filters out nulls.
   */
  resolveAll(
    skillId: string,
    trigger: EffectTrigger,
    ctx: ResolverContext,
  ): ResolvedEffect[] {
    const decls = this.getDeclarations(skillId, trigger);
    const results: ResolvedEffect[] = [];
    for (const decl of decls) {
      const resolved = this.resolve(skillId, decl, ctx);
      if (resolved) results.push(resolved);
    }
    return results;
  }

  // -------------------------------------------------------------------------
  // Condition evaluation
  // -------------------------------------------------------------------------

  evaluateCondition(condition: EffectCondition, ctx: ResolverContext): boolean {
    switch (condition.type) {
      case 'immunityCheck':
        // Passes when the target is NOT immune
        return !ctx.isImmune(condition.effectId);

      case 'hasUpgrade':
        return ctx.hasEffect(condition.skillId, condition.key);

      case 'paramGt0':
        return this.resolveParam(condition.param, ctx) > 0;

      case 'chargeRatio':
        return (ctx.chargeRatio ?? 0) >= condition.gte;

      case 'targetType':
        return ctx.targetType === condition.value;

      case 'defenseType':
        return ctx.defenseType === condition.value;

      case 'probability': {
        const chance = this.resolveParam(condition.chance, ctx);
        return Math.random() < chance;
      }

      default:
        return false;
    }
  }

  // -------------------------------------------------------------------------
  // Parameter resolution
  // -------------------------------------------------------------------------

  resolveParam(param: ParamSource, ctx: ResolverContext): number {
    switch (param.type) {
      case 'static':
        return param.value;

      case 'resolverAdditive':
        return ctx.getAdditiveEffect(param.skillId, param.key);

      case 'resolverMax':
        return ctx.getMaxEffect(param.skillId, param.key);

      case 'resolverMultiplicative': {
        const base = param.base ?? 1;
        return base * ctx.getMultiplicativeEffect(param.skillId, param.key);
      }

      case 'treePassive':
        return ctx.getTreePassiveEffect(param.tree, param.key, param.mode);

      case 'computed': {
        const baseVal = this.resolveParam(param.base, ctx);
        const bonusVal = this.resolveParam(param.bonus, ctx);
        return param.op === 'add' ? baseVal + bonusVal : baseVal * bonusVal;
      }

      default:
        return 0;
    }
  }
}
